import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { getUserProfile } from '../../../Services/MyProfile';
import { handlePostLoginFlow } from '../../../Utils/postLoginHandler';
import { useHeader } from '../../../Providers/HeaderContextProvider';
import InlineErrorMessage from '../../../Components/InlineErrorMessage';
import { ReactComponent as AppleIcon } from '../../../Assets/icon/apple.svg';

const AppleAuth = ({ onSuccess, onError }) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { addProfileData } = useHeader();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const appleScript = document.createElement('script');
    appleScript.src = 'https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js';
    appleScript.async = true;
    appleScript.defer = true;
    document.body.appendChild(appleScript);

    return () => {
      if (document.body.contains(appleScript)) {
        document.body.removeChild(appleScript);
      }
    };
  }, []);

  // Handle backend callback with tokens
  useEffect(() => {
    const handleBackendCallback = async () => {
      // Check if we're receiving tokens from backend redirect
      const token = searchParams.get('token');
      const refresh = searchParams.get('refresh');
      const isNewUser = searchParams.get('isNewUser') === 'true';
      const requiresPhoneVerification = searchParams.get('requiresPhoneVerification') === 'true';
      
      if (token && refresh) {
        setLoading(true);
        setError('');
        
        try {
          // Store tokens securely
          localStorage.setItem('token', token);
          localStorage.setItem('refreshToken', refresh);
          
          // Store user status for onboarding flow
          sessionStorage.setItem('isNewUser', isNewUser.toString());
          sessionStorage.setItem('requiresPhoneVerification', requiresPhoneVerification.toString());

          console.log('Apple Sign-In successful', {
            isNewUser,
            requiresPhoneVerification
          });

          // Update profile data in context
          if (addProfileData) {
            await addProfileData();
          }
          
          // Fetch fresh profile from API
          let profile = {};
          try {
            const freshProfile = await getUserProfile();
            if (freshProfile) {
              profile = freshProfile;
              localStorage.setItem('profile', JSON.stringify(freshProfile));
            }
          } catch (err) {
            console.error('Error fetching profile:', err);
            profile = JSON.parse(localStorage.getItem('profile') || '{}');
          }
          
          // Clean up URL parameters
          window.history.replaceState({}, document.title, window.location.pathname);
          
          // Check onboarding status (same logic as GoogleAuth)
          const onboardingSteps = profile?.onboarding_step || [];
          const hasCompletedOnboarding = Array.isArray(onboardingSteps) && 
            onboardingSteps.some(s => String(s).toLowerCase() === 'completed');
          const hasSubscription = Array.isArray(onboardingSteps) && 
            onboardingSteps.some(s => String(s).toLowerCase() === 'subscription');
          const isVerified = profile?.is_verified !== false;
          
          if (!isVerified || !hasCompletedOnboarding || !hasSubscription) {
            // New or unverified user - navigate to QR page
            const queryString = searchParams.toString();
            navigate(`/login/qr${queryString ? '?' + queryString : ''}`);
          } else {
            // Existing verified user - use handlePostLoginFlow like EmailAuth and GoogleAuth
            if (onSuccess) {
              onSuccess({ status: 200 });
            } else {
              await handlePostLoginFlow({ navigate, addProfileData, redirectUri: searchParams.get('redirect_uri') });
            }
          }
          
        } catch (error) {
          console.error('Apple callback error:', error);
          const errorMsg = error.message || 'Authentication processing failed. Please try again.';
          setError(errorMsg);
          if (onError) onError(errorMsg);
        } finally {
          setLoading(false);
        }
      }
    };

    handleBackendCallback();
  }, [searchParams, navigate, addProfileData, onSuccess, onError]);

  const handleAppleSignIn = async () => {
    if (loading) return;
    setLoading(true);
    setError('');

    try {
      if (typeof window.AppleID === 'undefined') {
        const errorMsg = 'Apple Sign-In is not available. Please try again later.';
        setError(errorMsg);
        if (onError) onError(errorMsg);
        setLoading(false);
        return;
      }

      // Get current frontend URL for backend to redirect back to
      const currentUrl = window.location.origin + window.location.pathname;
      
      // Encode the frontend callback URL in state parameter
      const state = btoa(`web:${btoa(currentUrl)}`);

      await window.AppleID.auth.init({
        clientId: 'com.aris.si', // Updated bundle ID
        scope: 'name email',
        redirectURI: process.env.REACT_APP_BASE_URL_AUTH + 'auth/v2/apple/callback',
        state: state,
        usePopup: false, // Use redirect flow for backend callback
      });

      // This will redirect to Apple, then to backend, then back to frontend with tokens
      await window.AppleID.auth.signIn();
      
    } catch (error) {
      console.error('Apple sign-in error:', error);
      if (error.error !== 'popup_closed_by_user') {
        const errorMsg = error.message || 'Apple sign-in failed. Please try again.';
        setError(errorMsg);
        if (onError) onError(errorMsg);
      }
      setLoading(false);
    }
  };

  return (
    <>
      <button 
        className="auth-method-btn"
        onClick={handleAppleSignIn}
        disabled={loading}
      >
        <AppleIcon style={{ width: '24px', height: '24px', marginRight: '0px' }} />
        <span style={{ flex: 1, textAlign: 'center' }}>
          {loading ? 'Signing in...' : 'Continue with Apple'}
        </span>
      </button>
      {error && <InlineErrorMessage message={error} />}
    </>
  );
};

export default AppleAuth;
